import { supabase, formatSupabaseError } from '../../supabase';
import { defaultSettings, currencyOptions } from '../../pages/admin/settings/settingsData';
import { saveAdminSettings, loadAdminSettings, setAdminSignupEmail } from '../../pages/admin/settings/settingsData';

const OPTIONAL_GYM_COLUMNS = [
  'tagline',
  'website',
  'opening_hours',
  'logo_url',
  'currency_symbol',
  'currency_position',
  'decimal_places',
];

function symbolForCode(code, fallback) {
  return currencyOptions.find((c) => c.code === code)?.symbol || fallback || 'Rs';
}

function mapGymRow(row, fallback = defaultSettings) {
  const code = row?.currency_code || fallback.currency?.code || defaultSettings.currency.code;
  const invoiceSequenceStart = Number(
    fallback?.invoiceSequenceStart ?? defaultSettings.invoiceSequenceStart,
  );
  return {
    gym: {
      name: row?.name || fallback.gym?.name || '',
      tagline: row?.tagline || fallback.gym?.tagline || '',
      address: row?.address || fallback.gym?.address || '',
      phone: row?.phone || fallback.gym?.phone || '',
      website: row?.website || fallback.gym?.website || '',
      openingHours: row?.opening_hours || fallback.gym?.openingHours || '',
      timezone: row?.timezone || fallback.gym?.timezone || 'Asia/Karachi',
    },
    currency: {
      code,
      symbol:
        row?.currency_symbol
        || fallback.currency?.symbol
        || symbolForCode(code, defaultSettings.currency.symbol),
      position: row?.currency_position || fallback.currency?.position || 'before',
      decimalPlaces: Number(
        row?.decimal_places ?? fallback.currency?.decimalPlaces ?? 0,
      ),
    },
    logoPreview: row?.logo_url || fallback.logoPreview || null,
    theme: fallback.theme || 'dark',
    invoiceSequenceStart: Number.isFinite(invoiceSequenceStart) && invoiceSequenceStart > 0
      ? Math.floor(invoiceSequenceStart)
      : defaultSettings.invoiceSequenceStart,
  };
}

function isMissingColumnError(error) {
  const message = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''}`;
  return /column|schema cache|Could not find/i.test(message);
}

function stripMentionedColumns(payload, error) {
  const message = `${error?.message || ''} ${error?.details || ''} ${error?.hint || ''}`;
  const next = { ...payload };
  let stripped = false;

  OPTIONAL_GYM_COLUMNS.forEach((col) => {
    if (col in next && message.includes(col)) {
      delete next[col];
      stripped = true;
    }
  });

  // If PostgREST only says "schema cache" without naming the column, drop all optional fields
  if (!stripped && isMissingColumnError(error)) {
    OPTIONAL_GYM_COLUMNS.forEach((col) => {
      if (col in next) {
        delete next[col];
        stripped = true;
      }
    });
  }

  return { next, stripped };
}

export async function fetchGymSettings(gymId) {
  if (!gymId) {
    return {
      ...defaultSettings,
      gym: { ...defaultSettings.gym },
      currency: { ...defaultSettings.currency },
    };
  }

  const local = loadAdminSettings(gymId);

  const { data, error } = await supabase
    .from('gyms')
    .select('*')
    .eq('id', gymId)
    .maybeSingle();

  if (error) {
    // Fall back to locally saved settings if schema is incomplete
    if (isMissingColumnError(error)) {
      return {
        ...defaultSettings,
        ...local,
        gym: { ...defaultSettings.gym, ...local.gym },
        currency: { ...defaultSettings.currency, ...local.currency },
      };
    }
    throw new Error(formatSupabaseError(error, 'Could not load gym settings.'));
  }

  if (!data) {
    return {
      ...defaultSettings,
      ...local,
      gym: { ...defaultSettings.gym, ...local.gym },
      currency: { ...defaultSettings.currency, ...local.currency },
    };
  }

  // Prefer local currency/logo/CR sequence when DB columns are not present yet
  const mapped = mapGymRow(data, local);
  if (!mapped.logoPreview && local.logoPreview) {
    mapped.logoPreview = local.logoPreview;
  }
  if (local.invoiceSequenceStart) {
    mapped.invoiceSequenceStart = local.invoiceSequenceStart;
  }
  saveAdminSettings(mapped, gymId);
  if (data.contact_email) setAdminSignupEmail(data.contact_email, gymId);
  return mapped;
}

async function updateGymRow(gymId, payload) {
  let current = { ...payload };
  let lastError = null;

  for (let attempt = 0; attempt < OPTIONAL_GYM_COLUMNS.length + 2; attempt += 1) {
    const { data, error } = await supabase
      .from('gyms')
      .update(current)
      .eq('id', gymId)
      .select('*')
      .maybeSingle();

    if (!error) return data;

    lastError = error;
    const { next, stripped } = stripMentionedColumns(current, error);
    if (!stripped) break;
    current = next;
  }

  throw new Error(formatSupabaseError(lastError, 'Could not save gym settings.'));
}

export async function saveGymSettings(gymId, settings) {
  if (!gymId) throw new Error('Gym context is missing. Sign in again.');

  const payload = {
    name: settings.gym?.name?.trim() || null,
    tagline: settings.gym?.tagline?.trim() || null,
    phone: settings.gym?.phone?.trim() || null,
    address: settings.gym?.address?.trim() || null,
    website: settings.gym?.website?.trim() || null,
    opening_hours: settings.gym?.openingHours?.trim() || null,
    timezone: settings.gym?.timezone || 'Asia/Karachi',
    currency_code: settings.currency?.code || 'PKR',
    currency_symbol: settings.currency?.symbol || 'Rs',
    currency_position: settings.currency?.position || 'before',
    decimal_places: Number(settings.currency?.decimalPlaces ?? 0),
    logo_url: settings.logoPreview || null,
  };

  let row = null;
  try {
    row = await updateGymRow(gymId, payload);
  } catch (err) {
    // Still persist locally so admin/receptionist currency works immediately
    saveAdminSettings(settings, gymId);
    throw err;
  }

  // Merge DB row with the form values so symbol/position/decimals stay even if columns were stripped
  const mapped = mapGymRow(row || {}, settings);
  mapped.currency = {
    code: settings.currency?.code || mapped.currency.code,
    symbol: settings.currency?.symbol || mapped.currency.symbol,
    position: settings.currency?.position || mapped.currency.position,
    decimalPlaces: Number(settings.currency?.decimalPlaces ?? mapped.currency.decimalPlaces ?? 0),
  };
  mapped.gym = {
    ...mapped.gym,
    ...settings.gym,
    name: settings.gym?.name?.trim() || mapped.gym.name,
  };
  mapped.logoPreview = settings.logoPreview ?? mapped.logoPreview;
  const invoiceStart = Number(settings.invoiceSequenceStart);
  mapped.invoiceSequenceStart = Number.isFinite(invoiceStart) && invoiceStart > 0
    ? Math.floor(invoiceStart)
    : (mapped.invoiceSequenceStart || defaultSettings.invoiceSequenceStart);

  saveAdminSettings(mapped, gymId);
  return mapped;
}
